import React, { Fragment } from "react";
import { cellGrow } from "constants/styles";
import { styles } from "components/HeroSection/styles";
import classnames from "classnames";
import Pill from "components/Pill";
import { useAppPagesTemplatesIndex, useCtxReferences } from "utils/selectors";
import { getIconForUID, SECTIONS } from "components/FormerEditor/utils";
import { interpolateRouteWithParams } from "utils/route";
import PATHS from "utils/paths";
import Link from "next/link";
import { matchSorter } from "match-sorter";
import { usePagePickerContext } from "utils/context/builder/page";
import isEmpty from "lodash/isEmpty";
import startCase from "lodash/startCase";
import {
  ORDERED_TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_DESCRIPTIONS
} from "utils/constants/template";
import SlimCTA from "components/SlimCTA";

const TypeNode = ({
  id,
  label,
  description,
  uuid,
  query,
  footer,
  status,
  callout,
  icon,
  disabled
}) => {
  const Icon = icon && getIconForUID(icon);
  const { application } = useCtxReferences();

  const destination = interpolateRouteWithParams(
    PATHS.BUILDER_APPLICATIONS_PAGES_SHOW_SECTION,
    {
      application,
      page: {
        uuid
      },
      section: {
        uuid: SECTIONS.PAGE_BUILDER
      }
    },
    query
  );

  return (
    <Link href={destination}>
      <div
        className={classnames(
          `${id} flex flex-column w-100 ba br2 hairline-1 pa3 mb3 mb-ns relative justify-between`,
          {
            "pointer cell-grow bg-white--hover ba--primary--hover": !disabled,
            "o-90 cursor-not-allowed": disabled
          }
        )}
      >
        <div className="flex flex-column">
          <div className="flex flex-row items-center">
            {Icon && <Icon size={24} />}
            <div
              className={classnames("f4 fw6 color-primary lh-title", {
                pl2: Icon
              })}
              style={{ marginTop: 2 }}
            >
              {label}
            </div>
          </div>
          <div className="f5 lh-copy pt2 mt1">{description}</div>
          {footer && <div className="f6 lh-copy pt2 mt1">{footer}</div>}
        </div>
        {callout && (
          <div className="pt2">
            <Pill
              status={status}
              copy={callout}
              customClasses={{
                container: "flex w-100 items-end justify-end"
              }}
            />
          </div>
        )}
      </div>
    </Link>
  );
};

const CategoryGrid = ({ category, items }) => {
  const nodes = items.map((template) => (
    <TypeNode key={template.id} {...template} />
  ));
  const description = TEMPLATE_CATEGORY_DESCRIPTIONS[category];
  return (
    <Fragment>
      <div className="f3 fw6 pb2">{startCase(category)}</div>
      {description && <div className="f5 lh-copy pb3">{description}</div>}
      <div className="grid-container w-100">{nodes}</div>
      <style jsx>{`
        @media (max-width: 30em) {
          .grid-container {
            display: flex;
            flex-direction: column;
          }
        }
        @media (min-width: 30em) {
          .grid-container {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            grid-template-rows: repeat(1, 1fr);
            gap: 16px 16px;
          }
        }
      `}</style>
      <style jsx>{cellGrow}</style>
      <style jsx>{styles}</style>
    </Fragment>
  );
};

/**
 * alias: UnauthPagePicker
 */
const PageTypePicker = ({ resetValues }) => {
  const templatesIndex = useAppPagesTemplatesIndex();

  const {
    values: { search, categories: selectedCategories }
  } = usePagePickerContext();
  const allTemplates = Object.values(templatesIndex);
  const hasSelectedCategories = !isEmpty(selectedCategories);
  const hasFilters = search || hasSelectedCategories;

  let filteredCollection = search
    ? matchSorter(allTemplates, search, {
        keys: ["label", "description"]
      })
    : allTemplates;

  /**
   * Filter collection by selected categories
   */
  if (hasSelectedCategories) {
    filteredCollection = filteredCollection.filter((collection) => {
      let match = false;
      if (collection && collection.categories) {
        for (const category in collection.categories) {
          match = selectedCategories[category];
          if (match) {
            break;
          }
        }
      }
      return match;
    });
  }

  /**
   * Ordered by categories
   */
  const content = [];
  for (
    let categoryIx = 0;
    categoryIx < ORDERED_TEMPLATE_CATEGORIES.length;
    categoryIx++
  ) {
    const category = ORDERED_TEMPLATE_CATEGORIES[categoryIx];

    const items = filteredCollection.filter(
      (collection) =>
        collection && collection.categories && collection.categories[category]
    );
    if (items.length) {
      content.push({
        category,
        items
      });
    }
  }
  const hasContent = content.length > 0;
  if (hasFilters && !hasContent) {
    return (
      <SlimCTA
        header="No results"
        subheader="Adjust your filters or query to get new results."
        actions={[
          {
            copy: "Reset",
            onClick: resetValues
          }
        ]}
      />
    );
  }

  return hasContent ? (
    <ul className="ma0 pa0">
      {content.map(({ category, items }, itemIx) => (
        <div key={category} className={itemIx ? "pt4 mt2" : ""}>
          <CategoryGrid category={category} items={items} />
        </div>
      ))}
    </ul>
  ) : null;
};
export default PageTypePicker;
